import React, { useState, useEffect, useRef } from "react";
import { observer } from "mobx-react";
import { Utils } from "Utils/utils";
import AutoCompleteDropdown from "Components/common/AutoCompleteDropdown";
import InputTextBox from "Components/common/InputTextBox";
import Button from "Components/common/Button";
import { confirmationPopupState } from "Components/common/confirmationPopup";
import { toasterState } from "Components/common/toaster";
import { accountsState } from "./AccountsState";
import "./accountListFilter.scss";

// Row renderer — mirrors the contacts CustomListBody (same class names, so
// the global common/_customList.scss styles both identically).
export const AccountListBody = props => {
  const { name = "", count = "", createTs = "" } = props || {};
  return (
    <>
      <div className="customListLeftSection">
        <span className="updatedName">{name}</span>
        <span className="createdDate">
          {name === "All accounts"
            ? "Default"
            : createTs
            ? `Created on ${Utils.formatDate(createTs)}`
            : ""}
        </span>
      </div>
      <div className="customListRightSection">
        {count !== "" && count !== null && count !== undefined && (
          <>
            <span className="totalContactInList">{count}</span>
            <span className="contactsField">Accounts</span>
          </>
        )}
      </div>
    </>
  );
};

export const CreateEditAccountListPopup = observer((props = {}) => {
  const { mode = "create", initialName = "", listId = "", onClose = () => {}, onSaved = () => {} } =
    props || {};
  const [name, setName] = useState(initialName);
  const [error, setError] = useState("");
  const [loader, setLoader] = useState(false);
  const inputBoxRef = useRef();

  useEffect(() => {
    inputBoxRef && inputBoxRef.current && inputBoxRef.current.focus();
  }, []);

  const handleNameChange = (id, val) => {
    error && setError("");
    setName(val);
  };

  const submit = async () => {
    const trimmed = name?.trim();
    if (!trimmed) {
      setError("Please enter the list name");
      return;
    }
    if (trimmed.toLowerCase() === "all accounts") {
      setError("List name already exists");
      return;
    }
    setLoader(true);
    const response =
      mode === "create"
        ? await accountsState.createAccountList(trimmed)
        : await accountsState.renameAccountList(listId, trimmed);
    setLoader(false);
    if (response?.data?.error) {
      setError("List name already exists");
      return;
    }
    await accountsState.fetchAccountLists();
    toasterState.setToastMsg(
      mode === "create" ? "List created successfully" : "List renamed successfully",
      "success"
    );
    if (mode === "create" && response?.data?.id) {
      accountsState.setActiveAccountListId(response.data.id);
    }
    onSaved();
    onClose();
  };

  return (
    <section className="createEditListPopupWrapper">
      <div className="card">
        <div className="popupHeader">
          <div className="title">
            {mode === "create" ? "Create List" : "Update List Name"}
          </div>
          <i className="material-icons commonPopupCloseIcon" onClick={onClose}>
            close
          </i>
        </div>
        <InputTextBox
          name="createAccountListInputBox"
          id="createAccountListInputBox"
          text="Enter List Name"
          type="text"
          value={name}
          change={handleNameChange}
          err={error}
          readOnly={false}
          inputBoxRef={inputBoxRef}
        />
        <Button
          id="createAccountListButton"
          name="createAccountListButton"
          btnText={mode === "create" ? "Create" : "Update"}
          type="button"
          className={`createListButton ${loader ? "loading" : ""}`}
          loader={loader}
          click={submit}
        />
      </div>
      <div className="common-popup-mask" onClick={onClose} />
    </section>
  );
});

export const CreateNewAccountList = observer((props = {}) => {
  const { setShowDropdown = () => {}, setShowCreatePopup = () => {} } =
    props || {};
  const handleClick = () => {
    setShowDropdown(false);
    setShowCreatePopup(true);
  };
  return (
    <div className="createNewListBtn" onClick={handleClick}>
      <i className="material-icons">add</i>
      <span className="btnText">Create new list</span>
    </div>
  );
});

/**
 * Account-list selector rendered INLINE with the "Accounts" page title
 * (HeaderLayout leftChildren) — the same header pattern Contacts uses.
 * Selection flows through accountsState so the table filters reactively.
 * Also owns create/rename/delete, mirroring dataSearch/CustomList's edit/
 * delete icons next to the dropdown.
 */
const AccountListDropdown = observer(() => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [showCreatePopup, setShowCreatePopup] = useState(false);
  const [showEditPopup, setShowEditPopup] = useState(false);

  useEffect(() => {
    accountsState.fetchAccountLists();
  }, []);

  const active = accountsState.accountLists.find(
    list => list.id === accountsState.activeAccountListId
  );

  const handleDelete = () => {
    if (!active?.id) return;
    confirmationPopupState.setPopupValues({
      content:
        "Are you sure you want to delete the list? Accounts in this list are still accessible from 'All accounts'.",
      actionBtnText: "Yes",
      cancelBtnText: "Cancel",
      loadingMsg: "Please wait...",
      cancelCbk: () => confirmationPopupState.setShowPopup(false),
      callback: async () => {
        const response = await accountsState.deleteAccountList(active.id);
        confirmationPopupState.setShowPopup(false);
        if (response?.data?.error) {
          toasterState.setToastMsg(
            "Something went wrong. Please try again later",
            "fail"
          );
          return;
        }
        accountsState.setActiveAccountListId("");
        await accountsState.fetchAccountLists();
        toasterState.setToastMsg("List deleted successfully", "success");
      }
    });
    confirmationPopupState.setShowPopup(true);
  };

  return (
    <section className="customListWrapper">
      <div className="customListContainer">
        <AutoCompleteDropdown
          optionList={[
            { id: "", name: "All accounts" },
            ...accountsState.accountLists
          ]}
          defaultValue={active?.name || "All accounts"}
          cbk={item =>
            accountsState.setActiveAccountListId(item && item.id ? item.id : "")
          }
          loader={false}
          showIcon={true}
          icon={
            <i className="material-icons-outlined customListIcon">
              format_list_bulleted
            </i>
          }
          updateListBody={true}
          Body={AccountListBody}
          showDropdown={showDropdown}
          setShowDropdown={setShowDropdown}
        >
          <CreateNewAccountList
            setShowDropdown={setShowDropdown}
            setShowCreatePopup={setShowCreatePopup}
          />
        </AutoCompleteDropdown>
        {active?.id && (
          <i
            className="material-icons list-icon editIcon"
            onClick={() => setShowEditPopup(true)}
          >
            edit
          </i>
        )}
        {active?.id && (
          <i className="material-icons list-icon deleteIcon" onClick={handleDelete}>
            delete
          </i>
        )}
      </div>
      {showCreatePopup && (
        <CreateEditAccountListPopup
          mode="create"
          onClose={() => setShowCreatePopup(false)}
        />
      )}
      {showEditPopup && active?.id && (
        <CreateEditAccountListPopup
          mode="edit"
          listId={active.id}
          initialName={active.name}
          onClose={() => setShowEditPopup(false)}
        />
      )}
    </section>
  );
});

export default AccountListDropdown;
